import React from "react";
import { useParams } from "react-router";
import { IconButton, Toast } from "@sscs/ui";
import { DeleteIcon } from "@sscs/ui/icons";
import { AllowedTo, Dialog } from "Components";
import { PERMISSION, REPORT_TYPE_REPRO } from "Constants";
import { useDialog, useReports } from "Hooks";
import { Version } from "Types";
import { apiErrorToast, generateReportKey } from "Utils";
import { useDeleteReproBuildMutation } from "../../../services/projects.api";

type DeleteReproBuildProps = {
  version: Version;
  className?: string;
};

const DeleteReproBuild = ({
  version,
  className,
}: DeleteReproBuildProps): React.JSX.Element => {
  const { groupId } = useParams();
  const deleteDialog = useDialog();
  const [deleteReproBuild] = useDeleteReproBuildMutation();
  const { closeReport } = useReports();

  return (
    <>
      <AllowedTo permissions={[PERMISSION.DELETE_VERSION]}>
        <IconButton
          id="delete-reproducible-build"
          className={className}
          size="small"
          tooltipTitle="Delete reproducible build"
          onClick={() => {
            deleteDialog.open();
          }}
        >
          <DeleteIcon />
        </IconButton>
      </AllowedTo>
      <Dialog
        title="Delete reproducible build"
        confirmText="Delete"
        cancelText="No"
        onConfirm={async () => {
          try {
            await deleteReproBuild({ versionId: version.version_id }).unwrap();

            Toast.show({
              title: "Reproducible build deleted",
              type: "success",
            });
          } catch (error) {
            apiErrorToast(error, "Could not delete reproducible build");
            deleteDialog.close();
            return;
          }

          closeReport(
            generateReportKey({
              groupId: groupId ?? "",
              projectName: version.project_name,
              packageName: version.package_name,
              version: version.software_info.version,
              build: REPORT_TYPE_REPRO,
            }),
          );
        }}
        onCancel={deleteDialog.close}
        isOpen={deleteDialog.isOpen}
        onClose={deleteDialog.close}
        cancelButtonColor="primary"
        confirmButtonColor="secondary"
      >
        <span>
          Are you sure you want to delete reproducible build for{" "}
          <b>
            {version.software_info.product} {version.software_info.version}
          </b>
          ?
        </span>
      </Dialog>
    </>
  );
};

export default DeleteReproBuild;
